import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  Flex,
  Text,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Modal,
  ModalOverlay,
  useColorMode,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Input,
  Switch,
  IconButton,
  Link,
  Select,
  Spacer,
  Tooltip,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  TabPanels,
  useTheme,
} from "@chakra-ui/react";
import { AddIcon, ExternalLinkIcon } from "@chakra-ui/icons";
import EditableText from "../EditableText/EditableText";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV } from "@fortawesome/free-solid-svg-icons";
import { cqAPI, getBid } from "../../lib/Auth";
import moment from "moment";
import History from "../HistorySettings/History";
import { useLocation, useNavigate } from "react-router-dom";

const sendHoursBefore = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24,
];

// ---- New: allow PDF + Word (DOC/DOCX) ----
const ALLOWED_MIMES = new Set([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
]);
const ALLOWED_EXTS = [".pdf", ".doc", ".docx"];
const hasAllowedExtension = (name = "") =>
  ALLOWED_EXTS.some((ext) => name.toLowerCase().endsWith(ext));
const isAllowedFile = (file) => ALLOWED_MIMES.has(file.type) || hasAllowedExtension(file.name);

// If you later return a PUBLIC https URL from the backend for stored files, you can
// enable live Office viewer previews by wrapping that URL with this helper.
const buildOfficePreviewUrl = (publicUrl) =>
  `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(publicUrl)}`;

const TAB_SLUGS = ["forms", "history"];

function slugToIndex(slug) {
  const i = TAB_SLUGS.findIndex((s) => s === (slug || "").toLowerCase());
  return i >= 0 ? i : 0;
}
function indexToHash(i) {
  return `#${TAB_SLUGS[i] || TAB_SLUGS[0]}`;
}

function Templates() {
  const [templates, setTemplates] = useState([]);
  const [currentTemplate, setCurrentTemplate] = useState(null); // State for current template being updated
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewName, setPreviewName] = useState("");
  const [sendOption, setSendOption] = useState("12");
  const [excludeSigned, setExcludeSigned] = useState(false);
  const [pMSWrite, setPMSWrite] = useState({});
  const fileInputRef = useRef(null);
  const toast = useToast();
  const { colorMode } = useColorMode();
  const bid = getBid();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [tabIndex, setTabIndex] = useState(0);
  const isAutoSendEnabled = (template) => (template.enabled?.[bid] && template.auto_send_to_new_patients && template.auto_send_to_new_patients[bid])

  useEffect(() => {
    const slug = (location.hash || "").replace(/^#/, "");
    setTabIndex(slugToIndex(slug));
  }, [location.hash]);

  const handleTabChange = (index) => {
    setTabIndex(index);
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: indexToHash(index),
      },
      { replace: false }
    );
  };

  const fetchTemplates = async () => {
    try {
      const response = await cqAPI.get("/settings/esign");
      setTemplates(response.data);
      // returns { type: 'ok', data: { pms_write: {...}, form_categories: [...] } }
      setPMSWrite(await cqAPI.get("/settings/esign/pms_write").then((d) => d.data || {}));
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  };

  // ---------- NEW: helpers for PMS Writeback controls ----------
  const formCategories = pMSWrite.form_categories || [];
  const consentCfg = (pMSWrite.pms_write && pMSWrite.pms_write.consent) || {};
  const historyCfg = (pMSWrite.pms_write && pMSWrite.pms_write.history) || {};
  const defaultCategory = formCategories[0]?.pms_id || "";

  const updatePmsWrite = async (key, next) => {
    // next: { enabled: boolean, category: string }
    try {
      const body = { [key]: { enabled: !!next.enabled, category: next.category || defaultCategory } };
      const res = await cqAPI.put("/settings/esign/pms_write", body);
      if (res?.type === "ok" || res?.status === 200) {
        setPMSWrite((prev) => ({
          ...prev,
          pms_write: {
            ...(prev.pms_write || {}),
            [key]: body[key],
          },
        }));
        toast({ title: "Updated", status: "success", duration: 4000, isClosable: true });
      } else {
        toast({
          title: "Error updating PMS writeback",
          description: res?.msg || "Unknown error",
          status: "error",
          duration: 7000,
          isClosable: true,
        });
      }
    } catch (e) {
      toast({ title: "Request failed", description: e.message, status: "error", duration: 7000, isClosable: true });
    }
  };

  const downloadFile = async (attachmentUrl, fileName) => {
    try {
      const downloadUrl = await cqAPI.downloadFile(attachmentUrl);
      if (!downloadUrl) {
        console.error("File unavailable for download:", attachmentUrl);
        return;
      }

      // Create a temporary link element and trigger the download
      const link = document.createElement("a");
      link.href = downloadUrl;
      // Keep the original filename + extension (don't force .pdf)
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();

      // Clean up by revoking the blob URL and removing the temporary link
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(link);
    } catch (error) {
      console.error("Failed to download file:", error);
    }
  };

  const handleFileButtonClick = (template = null) => {
    setCurrentTemplate(template); // Set the current template
    if (fileInputRef.current) fileInputRef.current.value = ""; // reset so same file can be re-uploaded
    fileInputRef.current?.click(); // Trigger file input click on button click
  };

  const uploadTemplate = async (event) => {
    const files = event.target.files;
    const uploadedFiles = [];
    for (let file of files) {
      if (!isAllowedFile(file)) {
        toast({
          title: "Invalid file type.",
          description: "Only PDF, DOC, or DOCX files are allowed.",
          status: "error",
          duration: 9000,
          isClosable: true,
        });
        continue;
      }
      const formData = new FormData();
      formData.append("file", file);
      try {
        const ep = currentTemplate
          ? `/settings/esign/upload-template?template_id=${currentTemplate._id}`
          : "/settings/esign/upload-template";
        const response = await cqAPI.post(ep, formData);
        if (response.type === "ok") {
          uploadedFiles.push({
            name: file.name || "Form",
            url: response.url,
            mimetype: file.type,
          });
        } else {
          toast({
            title: "Error uploading file.",
            description: response.msg,
            status: "error",
            duration: 9000,
            isClosable: true,
          });
          break;
        }
      } catch (error) {
        toast({
          title: "Error uploading file.",
          description: error.message,
          status: "error",
          duration: 9000,
          isClosable: true,
        });
        break;
      }
    }
    if (uploadedFiles.length > 0) {
      toast({
        title: "File(s) uploaded successfully.",
        status: "success",
        duration: 9000,
        isClosable: true,
      });
      await fetchTemplates();
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const updateTemplateField = async (template, field, value) => {
    const response = await cqAPI.post("/settings/esign", {
      template_id: template._id,
      field,
      value,
    });
    if (response.type === "error") {
      if (response.msg) {
        toast({
          title: "Error updating the template.",
          description: response.msg,
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      }
      return false;
    }
    await fetchTemplates();
    return true;
  };

  const handleSend = async () => {
    try {
      const response = await cqAPI.post("/forms/template/batchsend", {
        template_id: currentTemplate._id,
        ts: Date.now() - parseInt(sendOption) * 30 * 24 * 3600000,
        exclude: excludeSigned,
      });
      if (response.type === "ok") {
        toast({
          title: "Signature requests sent successfully.",
          status: "success",
          duration: 9000,
          isClosable: true,
        });
      } else if (response.msg) {
        toast({
          title: "Error sending signature requests.",
          description: response.msg,
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error sending signature requests.",
        description: error.message,
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    }
    setIsModalOpen(false);
  };

  // Reusable UI block for PMS writeback (switch + dropdown)
  const PmsWriteRow = ({
    title,
    configKey,
    config,
  }) => {
    const enabled = !!config.enabled;
    const value = config.category || defaultCategory;

    const onToggle = (e) => {
      const nextEnabled = e.target.checked;
      updatePmsWrite(configKey, { enabled: nextEnabled, category: value || defaultCategory });
    };

    const onChangeCategory = (e) => {
      const nextCategory = e.target.value;
      updatePmsWrite(configKey, { enabled, category: nextCategory });
    };

    return (
      <Box
        p={3}
        borderRadius="md"
        bgColor={colorMode === "light" ? "gray.100" : "gray.700"}
        mb={3}
      >
        <Flex align="center" gap={4} wrap="wrap" opacity={enabled ? 1 : 0.65}>
          <Tooltip label="Enable writing completed forms to your PMS" hasArrow>
            <Flex align="center" gap={2}>
              <Switch isChecked={enabled} onChange={onToggle} />
              <Text>Enabled</Text>
            </Flex>
          </Tooltip>
          <Text>{title}</Text>
          <Flex align="center" gap={2}>
            <Select
              value={value}
              onChange={onChangeCategory}
              isDisabled={!enabled}
              minW="260px"
            >
              {formCategories.map((c) => (
                <option key={c.pms_id} value={c.pms_id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Flex>
        </Flex>
      </Box>
    );
  };

  return (
    <Box p={4} maxHeight="100vh" width="100%" position="relative">
      <Tabs
        isFitted
        variant="enclosed"
        isLazy
        lazyBehavior="unmount"
        padding="8px 24px 24px 24px"
        bgColor={colorMode === "light" ? "#FFFFFF" : "#212330"}
        borderRadius="16px"
        index={tabIndex}
        onChange={handleTabChange}
      >
        <TabList mb="1em">
          <Tab
            padding="0px 16px 16px 16px"
            border="none"
            _selected={{
              borderBottom: `4px solid ${colorMode === "light"
                  ? theme.colors.brand[300]
                  : theme.colors.lightBackground
                }`,
            }}
          >
            Forms
          </Tab>
          <Tab
            padding="0px 16px 16px 16px"
            border="none"
            _selected={{
              borderBottom: `4px solid ${colorMode === "light"
                  ? theme.colors.brand[300]
                  : theme.colors.lightBackground
                }`,
            }}
          >
            Medical History
          </Tab>
        </TabList>
        <TabPanels>
          {/*--------------------------- FORMS TAB ----------------------------*/}
          <TabPanel>
            {/* NEW: Only show if form_categories not empty */}
            {formCategories.length > 0 && (
              <PmsWriteRow
                title="PMS Writeback (Consent Forms)"
                configKey="consent"
                config={consentCfg}
              />
            )}

            <Flex
              p={4}
              maxHeight="100vh"
              padding="8px 24px 24px 24px"
              height="95vh"
              bgColor={colorMode === "light" ? "#FFFFFF" : "#212330"}
              borderRadius="16px"
              flexDirection="column"
            >
              <Flex justifyContent="flex-end" mb={4}>
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={uploadTemplate}
                  hidden
                />
                <Button onClick={() => handleFileButtonClick()} leftIcon={<AddIcon />}>Add Form</Button>
              </Flex>
              {templates.map((template) => (
                <Box
                  className="template"
                  key={template._id}
                  mb="2"
                  padding="8px 16px 8px 16px"
                  bgColor={colorMode === "light" ? "gray.100" : "gray.700"}
                  borderRadius="md"
                  width="100%"
                  flexWrap="wrap"
                >
                  <Flex flexDirection="column" gap={2}>
                    <Flex flex="1" gap={2} alignItems="center">
                      <Flex minWidth="0" align="left" alignItems="center">
                        <EditableText
                          onSave={(newValue) => updateTemplateField(template, "name", newValue)}
                          initialValue={template.name}
                          placeholder="Name"
                          editable={template.bid === bid}
                          pattern={/[^ ]{3,}/}
                          invalid="more than 3 character."
                          truncate={true}
                        />
                      </Flex>
                      <Spacer />
                      <Flex alignSelf="flex-end">
                        <Menu>
                          <MenuButton as={IconButton} aria-label="Options" icon={<FontAwesomeIcon icon={faEllipsisV} />} variant="ellipsis" />
                          <MenuList>
                            <MenuItem
                              onClick={async (e) => {
                                e.preventDefault();
                                const downloaded = await cqAPI.downloadFile(template.url);
                                if (!downloaded) {
                                  return toast({
                                    title: "Cannot preview this file",
                                    description: "Something went wrong",
                                    status: "error",
                                    duration: 7000,
                                    isClosable: true,
                                  });
                                }
                                setPreviewName(template.name);
                                const isPdf = template.name?.toLowerCase().endsWith(".pdf");
                                if (isPdf) {
                                  setPreviewUrl(downloaded); // blob/object URL ok for PDFs
                                  setIsPreviewModalOpen(true);
                                } else {
                                  // For Word files, we need a publicly accessible URL to use Office viewer.
                                  // If your backend can provide one, replace `downloaded` with that public URL:
                                  // const publicUrl = await cqAPI.getPublicUrl(template.url);
                                  // setPreviewUrl(buildOfficePreviewUrl(publicUrl));
                                  // setIsPreviewModalOpen(true);
                                  toast({
                                    title: "Preview not available",
                                    description: "Word documents can’t be previewed here. Use Download instead.",
                                    status: "info",
                                    duration: 7000,
                                    isClosable: true,
                                  });
                                }
                              }}
                            >
                              Preview
                            </MenuItem>
                            {template.bid === bid &&
                              <MenuItem onClick={() => handleFileButtonClick(template)}>Update</MenuItem>}
                            <MenuItem>
                              <Link
                                onClick={(e) => {
                                  e.preventDefault();
                                  downloadFile(template.url, template.name);
                                }}
                                href="#"
                                target="_blank"
                                key={template.url}
                              >
                                Download
                                <ExternalLinkIcon ml="2" />
                              </Link>
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </Flex>
                    </Flex>
                    <Flex alignItems="center" gap={4}>
                      <Flex gap={4}>
                        {template.bid === bid ? (
                          <Flex gap={2}>
                            <Switch
                              isChecked={template.shared}
                              onChange={(e) => {
                                updateTemplateField(template, "shared", e.target.checked);
                              }}
                            />
                            <Tooltip
                              label="Share this form with other locations"
                              hasArrow
                            >
                              <Flex>
                                Share (with other locations)
                              </Flex>
                            </Tooltip>
                          </Flex>
                        ) : (
                          <Text>Shared by {template.owner}</Text>
                        )}
                      </Flex>
                    </Flex>
                    <Flex alignItems="center" gap={4} mt={4}>
                      <Flex gap={2}>
                        <Switch
                          isChecked={template.enabled?.[bid]}
                          onChange={(e) => {
                            updateTemplateField(template, "enabled", e.target.checked);
                          }}
                        />
                        <Flex>Active</Flex>
                      </Flex>
                    </Flex>
                    <Flex ml={2}>
                      <Tooltip
                        label={
                          template.hasCustomPlaceholders
                            ? "Cannot auto-send due to custom placeholders"
                            : "Select Active to enable this feature"
                        }
                        hasArrow
                        isDisabled={!template.hasCustomPlaceholders && template.enabled?.[bid]}
                      >
                        <Flex align="center" gap={2}>
                          <Switch
                            disabled={!template.enabled?.[bid] || template.hasCustomPlaceholders}
                            isChecked={isAutoSendEnabled(template)}
                            onChange={(e) => {
                              updateTemplateField(template, "auto_send_to_new_patients", e.target.checked);
                            }}
                          />
                          <Flex color={template.hasCustomPlaceholders ? "gray.500" : "inherit"}>
                            Auto Send to Patients
                          </Flex>
                        </Flex>
                      </Tooltip>
                    </Flex>
                    <Flex ml={4} gap={4}>
                      <Flex alignItems="left" gap={4}>
                        <Tooltip
                          label="Select Auto Send to enable this feature"
                          hasArrow
                          isDisabled={template.auto_send_to_new_patients && template.auto_send_to_new_patients[bid]}
                        >
                          <Flex align="center" gap={2}>
                            <Switch
                              disabled={!isAutoSendEnabled(template)}
                              isChecked={isAutoSendEnabled(template) && template.send_email && template.send_email[bid]}
                              onChange={(e) => {
                                updateTemplateField(template, "send_email", e.target.checked);
                              }}
                            />
                            <Flex color={!(template.auto_send_to_new_patients && template.auto_send_to_new_patients[bid]) && "grey"}>
                              Send Email
                            </Flex>
                          </Flex>
                        </Tooltip>
                        <Tooltip
                          label="Select Auto Send to enable this feature"
                          hasArrow
                          isDisabled={template.auto_send_to_new_patients && template.auto_send_to_new_patients[bid]}
                        >
                          <Flex align="center" gap={2}>
                            <Switch
                              disabled={!isAutoSendEnabled(template)}
                              isChecked={isAutoSendEnabled(template) && template.send_text && template.send_text[bid]}
                              onChange={(e) => {
                                updateTemplateField(template, "send_text", e.target.checked);
                              }}
                            />
                            <Flex color={!(template.auto_send_to_new_patients && template.auto_send_to_new_patients[bid]) && "grey"}>
                              Send SMS
                            </Flex>
                          </Flex>
                        </Tooltip>
                      </Flex>
                    </Flex>
                    <Flex ml={4}>
                      <Flex align="center" gap={2}>
                        <Flex
                          justifyContent="flex-start"
                          color={!isAutoSendEnabled(template) && "grey"}
                        >
                          Hours before next appointment
                        </Flex>
                        <Tooltip
                          label="Select Auto Send to enable this feature"
                          hasArrow
                          isDisabled={template.auto_send_to_new_patients && template.auto_send_to_new_patients[bid]}
                        >
                          <Flex>
                            <Select
                              disabled={!isAutoSendEnabled(template)}
                              value={
                                template.bid === bid
                                  ? template.send_hours_before_first_appointment
                                  : (template.shared_hours_before && template.shared_hours_before[bid]) ||
                                  template.send_hours_before_first_appointment ||
                                  1
                              }
                              onChange={(e) =>
                                updateTemplateField(
                                  template,
                                  "send_hours_before_first_appointment",
                                  parseInt(e.target.value)
                                )
                              }
                            >
                              {sendHoursBefore.map((o, i) => (
                                <option key={i} value={o}>
                                  {o}
                                </option>
                              ))}
                            </Select>
                          </Flex>
                        </Tooltip>
                      </Flex>
                    </Flex>
                    <Flex ml={2}>
                      <Tooltip label="Select Auto Send to enable this feature" hasArrow isDisabled={template.enabled?.[bid]}>
                        <Flex align="center" gap={2}>
                          <Switch
                            disabled={!isAutoSendEnabled(template)}
                            isChecked={
                              isAutoSendEnabled(template) &&
                              !(template.sign_latest_version && template.sign_latest_version[bid] === false)
                            }
                            onChange={(e) => {
                              updateTemplateField(template, "sign_latest_version", e.target.checked);
                            }}
                          />
                          <Flex>Patients must sign the latest version</Flex>
                        </Flex>
                      </Tooltip>
                    </Flex>
                    <Flex fontSize="14px">Updated: {template.updated? moment(template.updated).format("L") : moment(template.created).format("L")}</Flex>
                  </Flex>
                </Box>
              ))}
            </Flex>
            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
              <ModalOverlay />
              <ModalContent maxW="50vw" width="50vw">
                <ModalHeader>Send Signature Requests</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                  <Text mb={4}>
                    You are about to send a signature request to patients who have not received a request to sign the latest
                    version of this form.
                  </Text>
                  <Select
                    placeholder="Select time frame"
                    value={sendOption}
                    onChange={(e) => setSendOption(e.target.value)}
                    mb={4}
                  >
                    <option value="12">Only patients who have been to the office in the past 12 months</option>
                    <option value="18">Only patients who have been to the office in the past 18 months</option>
                    <option value="24">Only patients who have been to the office in the past 24 months</option>
                  </Select>
                  <Flex align="center" gap={2} mb={4}>
                    <Switch isChecked={excludeSigned} onChange={(e) => setExcludeSigned(e.target.checked)} mb={4} />
                    <Text>Exclude patients who have signed an older version of this form</Text>
                  </Flex>
                </ModalBody>
                <ModalFooter>
                  <Button variant="ghost" onClick={() => setIsModalOpen(false)}>
                    Cancel
                  </Button>
                  <Button colorScheme="blue" onClick={handleSend}>
                    Send
                  </Button>
                </ModalFooter>
              </ModalContent>
            </Modal>
            <Modal isOpen={isPreviewModalOpen} onClose={() => setIsPreviewModalOpen(false)} size="xl">
              <ModalOverlay />
              <ModalContent>
                <ModalHeader>{previewName || "File Preview"}</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                  {previewUrl ? (
                    <iframe
                      src={previewUrl}
                      width="100%"
                      height="600px"
                      style={{ border: "none", width: "100%", height: "500px", margin: "10px" }}
                    />
                  ) : (
                    <Text>No preview available.</Text>
                  )}
                </ModalBody>
              </ModalContent>
            </Modal>
          </TabPanel>
          {/*------------------------- MEDICAL HISTORY TAB --------------------------*/}
          <TabPanel>
            {/* NEW: Only show if form_categories not empty */}
            {formCategories.length > 0 && (
              <PmsWriteRow
                title="PMS Writeback (Medical History)"
                configKey="history"
                config={historyCfg}
              />
            )}
            <History />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
}

export default Templates;
